import React, { useEffect, useState, useMemo, memo, useRef } from "react";
import { useForm, FormProvider, Controller, useWatch } from "react-hook-form";
import { Button } from "primereact/button";
import FormField from "./FormField";
import { axios_helper_no_header } from "../../axios/AxiosHelper";
import { isReadOnlyMode } from "../../utils/UtilFunctions";
import "./form.css";
import useFocusFormElements from "./useFocusFormElements";
import { scrollToElementWithFirstFormError } from "./formUtils";
import { useLoader } from "../../hooks/LoaderContext";
import { AuthHeaderStorageKey } from "../../hooks/AuthContext";
import getBackendUrl from "../../../environment/BackendService";
import axios from "axios";
import { all } from "axios";

// Form file for common
const Form = ({
  formData,
  formFields,
  submitFormData,
  constraintErrors,
  mode,
  fetchLookupData = () => {},
  viewLookupDetails = () => {},
  lookupData = {},
}) => {
  const methods = useForm({
    defaultValues: {},
    shouldFocusError: true
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    getValues,
    reset,
    setError,
    clearErrors, 
    setFocus
  } = methods;

  const { isLoaderEnabled } =  useLoader();
  const formRef = useRef();
  const saveBtnRef = useRef();
  useFocusFormElements(formRef.current?.parentElement?.parentElement, mode);

  const openedDropdownsRef = useRef({});

   const [isInputFocussed, setIsInputFocussed] = useState(false);

   const isSaveBtnClicked = useRef(false);

   useEffect(() => {
     if (Object.keys(errors).length && isSaveBtnClicked.current) {
       isSaveBtnClicked.current = false;
       scrollToElementWithFirstFormError(errors, formRef);
     }
   }, [Object.keys(errors)]);

  useEffect(() => {
    if (!isLoaderEnabled && !Object.keys(errors).length && !isInputFocussed) {
      const timeout = setTimeout(() => {
        const firstInputField = document.querySelector(
          "input:not([data-input-not-focus]), select, textarea"
        );
        if (firstInputField) {
          firstInputField.focus();
          setIsInputFocussed(true);
        }
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [isLoaderEnabled, Object.keys(errors), isInputFocussed]);

  

  useEffect(() => {
    setIsInputFocussed(false);
  }, [mode, formData]);

  const dependentFieldNames = useMemo(() => {
    const dependentFieldsInfo = formFields.reduce((fieldNames, field) => {
      const { displayConditions } = field;
      if (displayConditions) {
        const dependentFieldNames = displayConditions.map(
          ({ dependentField }) => dependentField
        );
        dependentFieldNames.forEach((fieldName) => {
          if (!fieldNames.includes(fieldName)) {
            fieldNames.push(fieldName);
          }
        });
      }
      return [...fieldNames];
    }, []);
    return dependentFieldsInfo;
  }, [formFields]);

  const watchFormData = useWatch({ control, name: dependentFieldNames });
  const dropDownLabelsList = useMemo(() => {
    const filteredList = formFields
      .filter((field) => field.type === "dropdown")
      .map((field) => `${field.fieldLabel}${field.dropdownCondition}`);
    return [...new Set(filteredList)];
  }, [formFields]);

  const [DROPDOWN_OPTIONS, setDROPDOWN_OPTIONS] = useState({});

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const responseData = await axios.post(`${getBackendUrl()}api/dropdown-options`, { dropDowns: dropDownLabelsList }, {
          headers: JSON.parse(sessionStorage.getItem(AuthHeaderStorageKey))
        });
        setDROPDOWN_OPTIONS(responseData.data);
      } catch (error) {
        console.error("Failed to fetch dropdown options:", error);
      }
    };
    fetchDropdownOptions();
  }, [dropDownLabelsList]);

  useEffect(() => {
    reset({ ...formData });
  }, [formData, reset]);

useEffect(() => {
  const firstErrorField = constraintErrors?.[0]?.displayField;

  if (!firstErrorField) return;

  // Apply backend validation errors into RHF
  constraintErrors.forEach(({ displayField, errorMessage }) => {
    setError(displayField, {
      type: "custom",
      message: errorMessage
    });
  });

  // Delay ensures DOM + RHF renders finish
  setTimeout(() => {
    const el =
      document.querySelector(`[name="${firstErrorField}"]`) ||
      document.querySelector(`[data-form-field="${firstErrorField}"]`);

    if (!el) {
      // fallback to RHF built-in focus
      setFocus(firstErrorField);
      return;
    }

    // ---- Modern scroll solution with header offset ----
    const HEADER_OFFSET = 160; // <-- CHANGE THIS TO MATCH YOUR FIXED HEADER HEIGHT

    const elementTop = el.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: elementTop - HEADER_OFFSET,
      behavior: "smooth"
    });

    // Focus without auto-scrolling
    el.focus({ preventScroll: true });
  }, 300);
}, [constraintErrors, setError, setFocus]);

  useEffect(() => {
    const lookupDataKeys = Object.keys(lookupData);
    if (lookupDataKeys.length) {
      lookupDataKeys.forEach((key) => setValue(key, lookupData[key], { shouldValidate: true }));
    }
  }, [lookupData, setError]);

   useEffect(() => {
     const keyDownListener = (evt) => {
       const { key } = evt;
       if (
         key === "Enter" &&
         document.activeElement.tagName === "BODY" &&
         saveBtnRef.current
       ) {
         saveBtnRef.current?.click();
       } else if (key === "Home") {
        evt.preventDefault(); // Prevent default browser behavior (e.g., scrolling within an editable field)
        window.scrollTo({
          top: 0,
          behavior: "smooth", // Smooth scrolling
        });
      } else if (key === "End") {
        evt.preventDefault(); // Prevent default browser behavior
        window.scrollTo({
          top: document.documentElement.scrollHeight, // Scroll to the bottom of the document
          behavior: "smooth", // Smooth scrolling
        });
      }
     };
     window.addEventListener("keydown", keyDownListener, true);
     return () => window.removeEventListener("keydown", keyDownListener, true);
   }, []);

  const getDynamicFieldNature = (displayConditions) => {
    if (!displayConditions) return null;

    const fieldNatures = displayConditions.reduce((acc, condition) => {
      const {
        dependentField,
        dependentFieldValues,
        fieldNature,
        shouldFieldValuesIncluded,
      } = condition;

      const fieldValue = getValues(dependentField);

      // Check if the condition is met based on the included/excluded logic
      const isConditionMet =
        (shouldFieldValuesIncluded &&
          dependentFieldValues.includes(fieldValue)) ||
        (!shouldFieldValuesIncluded &&
          !dependentFieldValues.includes(fieldValue));

      if (isConditionMet) {
        acc.push(fieldNature); // Add the field nature if the condition is met
      }

      return acc;
    }, []);

    // Early return for highest priority
    if (fieldNatures.includes("Non-Display")) {
      return "hidden";
    }

    if (fieldNatures.includes("Read-only")) {
      return "readOnly";
    }

    if (fieldNatures.includes("Editable")) {
      return "editable";
    }

    return null; // If no field nature matches
  };

  const sectionedRowFields = useMemo(
    () =>
      formFields.reduce((acc, field) => {
        const section = field.section || "Default (hidden)";
        if (!acc[section]) {
          acc[section] = {};
        }
        const row = field.position[0];
        if (!acc[section][row]) {
          acc[section][row] = [];
        }
        const dynamicFieldNature = getDynamicFieldNature(
          field.displayConditions
        );
        acc[section][row].push({ ...field, dynamicFieldNature });
        return acc;
      }, {}),
    [formFields, watchFormData]
  );
  const showSection = (section, rows) => {
    const allFields = Object.values(rows)?.flat();
    const sectionHidden = allFields.every(
      (fields) => fields.dynamicFieldNature === "hidden"
    );
    if (section.includes("(Header-h4") && !sectionHidden){
      const sectionName = section.split("(Header-h4)")[0].trim();
      return <h4>{sectionName}</h4>;
    }
    else if (section.includes("(Header)") && !sectionHidden) {
      const sectionName = section.split("(Header)")[0].trim();
      return <h2>{sectionName}</h2>;
    }
    return null;
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => {
          isSaveBtnClicked.current = true;
          handleSubmit(submitFormData)(e);
        }}
        className={`p-fluid col col-12 ${mode} form`}
        autoComplete="off"
        aria-autocomplete="none"
        ref={formRef}
      >
        {Object.entries(sectionedRowFields).map(([section, rows]) => (
          <div
            key={section}
            className={`form-section ${section.split(" ")[0].toLowerCase()}`}
          >
            <>{showSection(section, rows)}</>
            {Object.entries(rows).map(([rowIndex, fields]) => (
              <div key={rowIndex} className="grid">
                {fields.map((field) => {
                  // console.log(field.name, dynamicFieldNature);
                  return (
                    <FormField
                      key={field.name}
                      field={field}
                      register={register}
                      getValues={getValues}
                      control={control}
                      errors={errors}
                      Controller={Controller}
                      dropdownOptions={
                        field.type === "dropdown"
                          ? DROPDOWN_OPTIONS[
                              `${field.fieldLabel}${field.dropdownCondition}`
                            ] || []
                          : []
                      }
                      mode={mode}
                      setValue={setValue}
                      fetchLookupData={fetchLookupData}
                      viewLookupDetails={viewLookupDetails}
                      dynamicFieldNature={field.dynamicFieldNature}
                      openedDropdownsRef={openedDropdownsRef}
                      setError={setError}
                      clearErrors={clearErrors}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        ))}
         {!isReadOnlyMode(mode) && (
          <Button
            label="Save"
            type="submit"
            severity="primary"
            className="form-submit-button"
            ref={saveBtnRef}
          />
        )}
      </form>
    </FormProvider>
  );
};

export default memo(Form);